import Shepherd from 'shepherd.js';
import { navigate } from '../router.js';

const TOUR_KEY = 'retroplay:tour-library-completed';

export function hasLibraryTourBeenCompleted() {
  return localStorage.getItem(TOUR_KEY) === '1';
}

function markTourCompleted() {
  localStorage.setItem(TOUR_KEY, '1');
}

export function resetLibraryTourState() {
  localStorage.removeItem(TOUR_KEY);
}

// Resolves once tour.css is usable. Shepherd renders its first step
// synchronously on start(), so callers must await this or the welcome modal
// paints unstyled until the stylesheet download finishes. Resolves on error
// or after 3s too: a hung request should degrade to a FOUC, not no tour.
function ensureTourCSS() {
  return new Promise((resolve) => {
    let timer = null;
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const watch = (link) => {
      link.addEventListener('load', done, { once: true });
      link.addEventListener('error', done, { once: true });
      timer = setTimeout(done, 3000);
    };
    const existing = document.getElementById('tour-css');
    if (existing) {
      if (existing.sheet) return resolve();
      return watch(existing);
    }
    const link = document.createElement('link');
    link.id = 'tour-css';
    link.rel = 'stylesheet';
    link.href = `/styles/tour.css?v=${typeof BUILD_SHA === 'string' ? BUILD_SHA : Date.now()}`;
    watch(link);
    document.head.appendChild(link);
  });
}

function pos(desktop, mobile = 'bottom') {
  return window.innerWidth <= 768 ? mobile : desktop;
}

function waitForEl(selector, timeout = 3000) {
  return new Promise((resolve) => {
    const start = Date.now();
    (function poll() {
      const el = document.querySelector(selector);
      if (el) return resolve(true);
      if (Date.now() - start > timeout) return resolve(false);
      requestAnimationFrame(poll);
    })();
  });
}

export async function startLibraryTour() {
  if (hasLibraryTourBeenCompleted()) return;

  await ensureTourCSS();

  const mobile = window.innerWidth <= 768;

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    exitOnEsc: true,
    keyboardNavigation: true,
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      canClickTarget: false,
      modalOverlayOpeningPadding: 8,
      modalOverlayOpeningRadius: 6,
    },
  });

  // ── Part A: Browse Screen ──────────────────────────────────────────

  tour.addStep({
    id: 'welcome',
    title: 'Welcome to the Library',
    text: 'Browse the full RetroPlay catalog: filter, search, and discover games across every system.',
    buttons: [
      { text: 'Skip Tour', action: () => tour.cancel(), classes: 'shepherd-button-secondary' },
      { text: "Let's Go", action: () => tour.next() },
    ],
  });

  tour.addStep({
    id: 'system-chips',
    attachTo: { element: '.lib-system-chips', on: 'bottom' },
    title: 'Filter by System',
    text: 'Tap a system chip to filter the catalog. The count shows how many games are available for each.',
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  tour.addStep({
    id: 'search',
    attachTo: { element: '.lib-search', on: 'bottom' },
    title: 'Search',
    text: 'Start typing to find any game instantly.',
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  tour.addStep({
    id: 'sort-view',
    attachTo: { element: '.lib-index-topbar', on: 'bottom' },
    title: 'Sort & View',
    text: 'Sort games A–Z, by recently played, favorites first, or highest rated. Switch between grid and list views.',
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  if (mobile) {
    tour.addStep({
      id: 'filters-mobile',
      attachTo: { element: '.lib-filter-drawer-toggle', on: 'bottom' },
      title: 'Filters',
      text: 'Tap here to open the filter panel: filter by genre, toggle "Favorites only" or "Has achievements", and more.',
      showOn: () => !!document.querySelector('.lib-filter-drawer-toggle'),
      buttons: [
        { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
        { text: 'Next', action: () => tour.next() },
      ],
    });
  } else {
    tour.addStep({
      id: 'sidebar',
      attachTo: { element: '.lib-sidebar', on: 'right' },
      title: 'Filters',
      text: 'Filter by genre, toggle options like "Favorites only" or "Has achievements", and jump to any letter in the alphabet.',
      showOn: () => !!document.querySelector('.lib-sidebar'),
      buttons: [
        { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
        { text: 'Next', action: () => tour.next() },
      ],
    });

    tour.addStep({
      id: 'custom-filters',
      attachTo: { element: '.lib-sidebar-saved-filters', on: 'right' },
      title: 'Custom Filters',
      text: 'Save filter combinations you use often. Hit the + to create a new one, or edit an existing filter with the pencil icon.',
      showOn: () => {
        const el = document.querySelector('.lib-sidebar-saved-filters');
        return el && el.offsetParent !== null;
      },
      buttons: [
        { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
        { text: 'Next', action: () => tour.next() },
      ],
    });

    tour.addStep({
      id: 'lists',
      attachTo: { element: '.lib-sidebar-lists', on: 'right' },
      title: 'Your Lists',
      text: 'Organize games into custom lists: wishlists, favorites by genre, challenge runs, whatever you like.',
      showOn: () => {
        const el = document.querySelector('.lib-sidebar-lists');
        return el && el.offsetParent !== null;
      },
      buttons: [
        { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
        { text: 'Next', action: () => tour.next() },
      ],
    });
  }

  // ── Trending strip (browse screen, conditional) ────────────────────

  tour.addStep({
    id: 'trending',
    attachTo: { element: '.lib-trending-section', on: 'bottom' },
    title: 'Trending This Week',
    text: 'See what the community is playing right now. Tap any cover to jump straight in.',
    beforeShowPromise: () => waitForEl('.lib-trending-strip', 1500),
    showOn: () => !!document.querySelector('.lib-trending-strip'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  // ── Transition: open a game detail ─────────────────────────────────

  tour.addStep({
    id: 'game-cards',
    attachTo: { element: '.lib-tile', on: 'bottom' },
    title: 'Game Cards',
    text: 'Hover (or tap) any game for quick actions: Play, add it to a list, or +Q to drop it in your Queue. Let\'s open one to see the details.',
    showOn: () => !!document.querySelector('.lib-tile'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      {
        text: 'Next',
        action: () => {
          const tile = document.querySelector('.lib-tile');
          if (!tile) { tour.next(); return; }
          const link = tile.querySelector('a[href*="/library/g/"]');
          if (link) {
            const path = new URL(link.href, location.origin).pathname;
            navigate(path);
          } else {
            tile.click();
          }
          tour.next();
        },
      },
    ],
  });

  // ── Part B: Game Detail ────────────────────────────────────────────

  tour.addStep({
    id: 'hero-actions',
    attachTo: { element: '.lib-game-hero-actions', on: 'bottom' },
    title: 'Play, Favorite & List',
    text: 'Hit Play to jump right in. Star a game to favorite it, or add it to a custom list.',
    beforeShowPromise: () => waitForEl('.lib-game-hero-actions'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  // ── My Queue (game detail, conditional) ────────────────────────────

  tour.addStep({
    id: 'queue',
    attachTo: { element: '.lib-game-queue-btn', on: 'bottom' },
    title: 'My Queue',
    text: 'Tap +Q here (or on any game tile) to line up what to play next. Your Queue shows up as "Up Next" on the home screen and as a list you can reorder anytime.',
    beforeShowPromise: () => waitForEl('.lib-game-queue-btn', 1000),
    showOn: () => !!document.querySelector('.lib-game-queue-btn'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  tour.addStep({
    id: 'achievements',
    attachTo: { element: '.lib-game-section-achievements', on: 'bottom' },
    title: 'Achievements',
    text: 'Track your progress on achievements: see which ones you\'ve unlocked and how rare they are.',
    beforeShowPromise: () => waitForEl('.lib-game-section-achievements', 500),
    showOn: () => !!document.querySelector('.lib-game-section-achievements'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  tour.addStep({
    id: 'tags',
    attachTo: { element: '.lib-game-tags-section', on: 'bottom' },
    title: 'Tags & Genres',
    text: 'Click any tag to filter the library by that genre, theme, or mechanic.',
    beforeShowPromise: () => waitForEl('.lib-game-tags-section', 500),
    showOn: () => !!document.querySelector('.lib-game-tags-section'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  tour.addStep({
    id: 'related',
    attachTo: { element: '.lib-game-related', on: 'bottom' },
    title: 'Related Games',
    text: 'Discover similar games. Click one to jump straight to its details.',
    beforeShowPromise: () => waitForEl('.lib-game-related', 500),
    showOn: () => !!document.querySelector('.lib-game-related'),
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Next', action: () => tour.next() },
    ],
  });

  // ── Finish ─────────────────────────────────────────────────────────

  tour.addStep({
    id: 'finish',
    title: "You're All Set!",
    text: "That's the library tour. Explore, build lists, and track your progress across thousands of retro games.",
    buttons: [
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      { text: 'Finish', action: () => tour.complete() },
    ],
  });

  tour.on('complete', markTourCompleted);
  tour.on('cancel', markTourCompleted);

  tour.start();
}
